import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/forgot-password`, {
        email: email.trim().toLowerCase()
      });

      if (response.data.success) {
        setMessage('Password reset instructions have been sent to your email address.');
        setEmail('');
      } else {
        setError(response.data.message || 'Failed to send reset instructions');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'An error occurred. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-vh-100 d-flex align-items-center py-5 public-page" 
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative'
      }}
    >
      <div 
        className="position-absolute top-0 start-0 w-100 h-100"
        style={{
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3
        }}
      />
      
      <Container className="position-relative">
        <Row className="justify-content-center">
          <Col lg={5} md={7} sm={9}>
            <Card className="border-0 shadow-lg" style={{ backdropFilter: 'blur(10px)' }}>
              <Card.Body className="p-5">
                <div className="text-center mb-5">
                  <div className="bg-primary rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                       style={{ width: '80px', height: '80px' }}>
                    <i className="fas fa-key text-white" style={{ fontSize: '2rem' }}></i>
                  </div>
                  <h2 className="fw-bold text-dark mb-2">Reset Password</h2>
                  <p className="text-muted">
                    Enter your email address and we'll send you instructions to reset your password.
                  </p>
                </div>

                {error && (
                  <Alert variant="danger" className="mb-4 border-0">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {error}
                  </Alert>
                )}

                {message && (
                  <Alert variant="success" className="mb-4 border-0">
                    <i className="fas fa-check-circle me-2"></i>
                    {message}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold text-dark">Email Address</Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="border-0 bg-light">
                        <i className="fas fa-envelope text-muted"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your registered email address"
                        required
                        disabled={loading}
                        className="border-0 bg-light py-3"
                        style={{ fontSize: '1rem' }}
                      />
                    </InputGroup>
                    <Form.Text className="text-muted mt-2">
                      <i className="fas fa-info-circle me-1"></i>
                      We'll send reset instructions to this email address
                    </Form.Text>
                  </Form.Group>

                  <div className="d-grid mb-4">
                    <Button 
                      type="submit" 
                      variant="primary" 
                      size="lg" 
                      disabled={loading || !email.trim()}
                      className="py-3 fw-semibold"
                      style={{ 
                        background: 'linear-gradient(45deg, #667eea, #764ba2)',
                        border: 'none',
                        fontSize: '1.1rem'
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                          Sending Instructions...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane me-2"></i>
                          Send Reset Instructions
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="text-center">
                    <p className="text-muted mb-0">
                      Remember your password?{' '}
                      <Link 
                        to="/login" 
                        className="text-decoration-none fw-semibold"
                        style={{ color: '#667eea' }}
                      >
                        Sign In
                      </Link>
                    </p>
                  </div>
                </Form>

                <div className="mt-4 pt-4 border-top text-center">
                  <small className="text-muted">
                    Having trouble? Contact our support team at{' '}
                    <a href="mailto:support@networqpay.com" className="text-decoration-none" style={{ color: '#667eea' }}>
                      support@networqpay.com
                    </a>
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ForgotPassword;